import { Link } from 'react-router-dom';
import { Shield, Phone, CheckCircle, Flame, HardHat, Factory, Moon, AlertTriangle, Target, Plane, FileCheck, Radio, UserX, Route, Car, Calendar, Globe, Eye, Home, Briefcase } from 'lucide-react';
import { SEO } from '../components/SEO';
import { K9Section } from '../components/services/K9Section';
import { ArmedUnarmedSection } from '../components/services/ArmedUnarmedSection';
import { PatrolSection } from '../components/services/PatrolSection';
import { CCTVSection } from '../components/services/CCTVSection';

const executive = "https://customer-assets.emergentagent.com/job_surfside-guard/artifacts/ouzxb1wd_IMG_1274.jpeg";
const drone = "https://images.unsplash.com/photo-1692527021975-23fdcc191482?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxESkklMjBNYXRyaWNlJTIwMzBUJTIwZW50ZXJwcmlzZSUyMGRyb25lfGVufDB8fHx8MTc2OTk3MDgxN3ww&ixlib=rb-4.1.0&q=85";

// Expanded Executive Protection Section Component
const ExecutiveProtectionSection = () => {
  const epServices = [
    {
      icon: <UserX className="h-6 w-6" />,
      title: 'Hostile Removal & De-escalation',
      description: 'Trained operatives skilled in safely removing hostile, aggressive, or non-compliant individuals from your property, workplace, or event. Our team uses lawful, controlled techniques that prioritize safety for all parties while maintaining professionalism. We handle trespassers, disgruntled employees, uninvited guests, and volatile situations with measured force and composure.',
      ideal: 'Ideal for corporate offices, retail locations, events, and private properties.'
    },
    {
      icon: <Route className="h-6 w-6" />,
      title: 'Advance Security Planning',
      description: 'Every executive protection detail begins with thorough advance work. Our team conducts pre-arrival reconnaissance of venues, routes, and residences. We identify threat vulnerabilities, establish emergency extraction points, coordinate with local authorities, and develop contingency plans for every scenario. Nothing is left to chance.',
      ideal: 'Essential for high-profile appearances, travel, and corporate events.'
    },
    {
      icon: <Car className="h-6 w-6" />,
      title: 'Secure Transportation',
      description: 'Our protection agents provide secure ground transportation including defensive and evasive driving techniques, route planning with primary and alternate paths, vehicle counter-surveillance sweeps, and real-time threat monitoring during transit. We ensure safe movement from point A to point B, whether across town or across state lines.',
      ideal: 'Perfect for executives, VIPs, and high-net-worth individuals.'
    },
    {
      icon: <Calendar className="h-6 w-6" />,
      title: 'Event & Venue Security',
      description: 'Comprehensive close protection coverage for corporate functions, red carpet events, private parties, product launches, and public appearances. Our agents integrate seamlessly with event staff, control access points, monitor crowd dynamics, and maintain a protective perimeter — all while keeping a low profile.',
      ideal: 'Ideal for galas, conferences, premieres, and private gatherings.'
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: 'Travel Security Management',
      description: 'End-to-end security coordination for domestic and international travel. We handle pre-trip intelligence briefings, airport and hotel security coordination, in-country threat monitoring, and safe transportation logistics. Our agents accompany clients or operate remotely to ensure seamless protection throughout the entire itinerary.',
      ideal: 'Critical for business travel, international trips, and high-risk destinations.'
    },
    {
      icon: <Eye className="h-6 w-6" />,
      title: 'Threat Assessment & Intelligence',
      description: 'Proactive risk analysis including background investigations on potential threats, social media monitoring, stalker case management, and ongoing threat level evaluations. We identify risks before they materialize and develop mitigation strategies to keep our clients ahead of potential dangers.',
      ideal: 'Recommended for public figures, executives, and anyone facing specific threats.'
    },
    {
      icon: <Home className="h-6 w-6" />,
      title: 'Residential & Estate Protection',
      description: 'Round-the-clock protection for your home and family. Our residential security details include perimeter monitoring, access control, visitor screening, CCTV integration, and coordinated response with local law enforcement. We secure your private space so you can live without worry.',
      ideal: 'Ideal for estates, luxury homes, and family protection.'
    },
    {
      icon: <Briefcase className="h-6 w-6" />,
      title: 'Corporate Security Consulting',
      description: 'We partner with businesses to develop comprehensive workplace security programs — from workplace violence prevention and termination security to executive safety training and emergency action plans. Our consultants assess your organizational risks and implement protocols that protect your people and your brand.',
      ideal: 'Perfect for corporations, law firms, and high-value enterprises.'
    }
  ];

  const qualifications = [
    { icon: <Shield className="h-5 w-5" />, text: 'Licensed California PPO #15982' },
    { icon: <Target className="h-5 w-5" />, text: 'Advanced Defensive Tactics Certified' },
    { icon: <FileCheck className="h-5 w-5" />, text: 'Fully Bonded & Insured' },
    { icon: <Radio className="h-5 w-5" />, text: '24/7 Dispatch & Coordination' },
    { icon: <CheckCircle className="h-5 w-5" />, text: 'Background Verified Operatives' },
    { icon: <AlertTriangle className="h-5 w-5" />, text: 'Emergency Medical Response Trained' }
  ];

  return (
    <div id="executive-protection" className="py-20 bg-[#0A0A0A]" data-testid="service-section-executive-protection">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-2">Close Protection Services</p>
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
              Executive Protection.<br /><span className="text-[#F5C518]">Unmatched Precision.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-6">
              Our executive protection division provides elite close protection services for high-profile individuals, corporate executives, celebrities, and VIPs. From hostile removal and threat neutralization to secure transportation and advance planning — our operatives are trained to handle any scenario with professionalism and decisive action.
            </p>
            <div className="bg-[#121212] border-l-4 border-[#F5C518] p-4 mb-6">
              <p className="text-white font-semibold">More than a bodyguard.</p>
              <p className="text-[#F5C518]">A complete protective intelligence operation.</p>
            </div>
            <Link to="/contact" className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-[#E5B508] transition-all duration-300" data-testid="service-cta-executive-protection">
              <span>Request Consultation</span>
            </Link>
          </div>
          <div className="relative">
            <img src={executive} alt="Executive Protection Agent" className="w-full aspect-[4/3] object-cover border border-zinc-800" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/50 to-transparent" />
          </div>
        </div>

        {/* Service Capabilities */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold uppercase tracking-tight text-white mb-8 font-['Barlow_Condensed'] text-center">
            Our <span className="text-[#F5C518]">Capabilities</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {epServices.map((service) => (
              <div key={service.title} className="bg-[#121212] border border-zinc-800 p-6 hover:border-[#F5C518]/50 transition-colors">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#F5C518]/10 text-[#F5C518] mb-4">
                  {service.icon}
                </div>
                <h4 className="text-lg font-bold text-white mb-3 font-['Barlow_Condensed'] uppercase">{service.title}</h4>
                <p className="text-zinc-400 text-sm mb-3">{service.description}</p>
                <p className="text-[#F5C518] text-xs font-semibold">{service.ideal}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Qualifications & Hostile Removal Detail */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <div className="bg-[#121212] border border-zinc-800 p-8">
            <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
              Team <span className="text-[#F5C518]">Qualifications</span>
            </h3>
            <div className="space-y-4">
              {qualifications.map((item) => (
                <div key={item.text} className="flex items-center text-zinc-400">
                  <span className="text-[#F5C518] mr-3">{item.icon}</span>
                  {item.text}
                </div>
              ))}
            </div>
            <p className="text-zinc-500 text-sm mt-6 pt-6 border-t border-zinc-800">
              Every operative undergoes rigorous vetting, continuous training, and performance evaluation.
            </p>
          </div>

          <div className="bg-[#121212] border border-zinc-800 p-8">
            <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
              Hostile Removal <span className="text-[#F5C518]">Protocol</span>
            </h3>
            <p className="text-zinc-400 mb-4">Our hostile removal process follows a structured escalation model:</p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Verbal de-escalation and compliance requests
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Lawful trespass warnings and documentation
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Controlled physical escort from premises
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Law enforcement coordination when required
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Post-incident reporting and evidence preservation
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Follow-up threat monitoring and restraining order support
              </li>
            </ul>
            <p className="text-[#F5C518] text-sm font-semibold">
              Safety first. Every action is lawful, documented, and proportional.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-[#121212] border border-[#F5C518]/30 p-8 text-center">
          <h3 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
            Protect What <span className="text-[#F5C518]">Matters</span>
          </h3>
          <p className="text-zinc-400 mb-6 max-w-2xl mx-auto">
            Request a confidential consultation to discuss your protection needs. We develop tailored security plans based on your unique risk profile and lifestyle.
          </p>
          <Link to="/contact" className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#E5B508] transition-all duration-300">
            <span>Contact Us Today</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Expanded Drone Section Component
const DroneSection = () => {
  const droneServices = [
    {
      icon: <Flame className="h-6 w-6" />,
      title: 'Thermal Perimeter Security Sweeps',
      description: 'Nighttime and low-visibility aerial scans using advanced infrared technology to detect unauthorized persons, fence line breaches, hidden movement in vegetation, rooftop intrusions, and remote property access points.',
      ideal: 'Ideal for construction sites, industrial yards, estates, and large properties.'
    },
    {
      icon: <HardHat className="h-6 w-6" />,
      title: 'Construction Theft Prevention',
      description: 'Copper theft and equipment loss cost contractors thousands. We provide scheduled or on-demand aerial thermal sweeps to deter criminal activity, support on-site guards, document vulnerabilities, and increase visible deterrence.',
      ideal: 'Perfect for active construction sites and equipment yards.'
    },
    {
      icon: <Factory className="h-6 w-6" />,
      title: 'Industrial & Critical Infrastructure',
      description: 'We assist industrial clients with tank heat anomaly detection, equipment hotspot identification, after-hours perimeter scans, and temporary fire-watch thermal monitoring.',
      ideal: 'Perfect for refineries, warehouses, utility sites, and large storage facilities.'
    },
    {
      icon: <Moon className="h-6 w-6" />,
      title: 'Executive & Estate Security Sweeps',
      description: 'For high-net-worth property owners: pre-event perimeter clearing, woodland thermal scans, vulnerability detection, and discreet aerial surveillance support.',
      ideal: 'Ideal for estates, private properties, and VIP residences.'
    },
    {
      icon: <AlertTriangle className="h-6 w-6" />,
      title: 'Emergency Response Support',
      description: 'On-call aerial thermal capability for missing person support, post-incident area assessment, and disaster property scans. Services provided in coordination with appropriate authorities where required.',
      ideal: 'Available 24/7 for emergency situations.'
    }
  ];

  const whyChooseUs = [
    { icon: <Shield className="h-5 w-5" />, text: 'Licensed Security Management' },
    { icon: <Plane className="h-5 w-5" />, text: 'FAA Certified Remote Pilot (Part 107)' },
    { icon: <Radio className="h-5 w-5" />, text: 'Enterprise Thermal Drone Platform' },
    { icon: <FileCheck className="h-5 w-5" />, text: 'Fully Insured Operations' },
    { icon: <Target className="h-5 w-5" />, text: 'Safety-Compliant Procedures' },
    { icon: <CheckCircle className="h-5 w-5" />, text: 'Professional Reporting & Documentation' }
  ];

  const whoWeServe = [
    'Construction Companies',
    'Industrial Facilities',
    'Storage & Logistics Yards',
    'Estate Owners',
    'Commercial Property Managers',
    'Agricultural & Rural Property Owners'
  ];

  return (
    <div id="drone-surveillance" className="py-20 bg-[#0A0A0A]" data-testid="service-section-drone-surveillance">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-2">Aerial Thermal Security & Risk Intelligence</p>
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
              Advanced Surveillance.<br /><span className="text-[#F5C518]">Elevated Protection.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-6">
              We provide licensed security services enhanced by enterprise-grade aerial thermal technology. Our drone division delivers real-time thermal detection, perimeter intelligence, and industrial risk assessment for clients who require more than traditional ground patrol.
            </p>
            <div className="bg-[#121212] border-l-4 border-[#F5C518] p-4 mb-6">
              <p className="text-white font-semibold">This is not photography.</p>
              <p className="text-[#F5C518]">This is operational aerial security support.</p>
            </div>
            <Link to="/contact" className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-[#E5B508] transition-all duration-300" data-testid="service-cta-drone-surveillance">
              <span>Request Consultation</span>
            </Link>
          </div>
          <div className="relative">
            <img src={drone} alt="Enterprise Thermal Drone" className="w-full aspect-[4/3] object-cover border border-zinc-800" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/50 to-transparent" />
          </div>
        </div>

        {/* What We Do */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold uppercase tracking-tight text-white mb-8 font-['Barlow_Condensed'] text-center">
            What We <span className="text-[#F5C518]">Do</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {droneServices.map((service) => (
              <div key={service.title} className="bg-[#121212] border border-zinc-800 p-6 hover:border-[#F5C518]/50 transition-colors">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#F5C518]/10 text-[#F5C518] mb-4">
                  {service.icon}
                </div>
                <h4 className="text-lg font-bold text-white mb-3 font-['Barlow_Condensed'] uppercase">{service.title}</h4>
                <p className="text-zinc-400 text-sm mb-3">{service.description}</p>
                <p className="text-[#F5C518] text-xs font-semibold">{service.ideal}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Why Choose Us & Equipment */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <div className="bg-[#121212] border border-zinc-800 p-8">
            <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
              Why Choose <span className="text-[#F5C518]">Us</span>
            </h3>
            <div className="space-y-4">
              {whyChooseUs.map((item) => (
                <div key={item.text} className="flex items-center text-zinc-400">
                  <span className="text-[#F5C518] mr-3">{item.icon}</span>
                  {item.text}
                </div>
              ))}
            </div>
            <p className="text-zinc-500 text-sm mt-6 pt-6 border-t border-zinc-800">
              We combine security expertise with aerial intelligence — not hobbyist drone work.
            </p>
          </div>

          <div className="bg-[#121212] border border-zinc-800 p-8">
            <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
              Equipment <span className="text-[#F5C518]">Capability</span>
            </h3>
            <p className="text-zinc-400 mb-4">Our enterprise drone platform includes:</p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2" />
                High-resolution thermal imaging (640×512)
              </li>
              <li className="flex items-center text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2" />
                48MP zoom camera with 16x optical zoom
              </li>
              <li className="flex items-center text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2" />
                Laser rangefinder (up to 1200m)
              </li>
              <li className="flex items-center text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2" />
                IP55 weather-resistant flight system
              </li>
              <li className="flex items-center text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2" />
                Night operational capability
              </li>
              <li className="flex items-center text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2" />
                41-minute flight time
              </li>
            </ul>
            <p className="text-[#F5C518] text-sm font-semibold">
              This allows us to detect what traditional patrol cannot see.
            </p>
          </div>
        </div>

        {/* Who We Serve */}
        <div className="text-center mb-12">
          <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
            Who We <span className="text-[#F5C518]">Serve</span>
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {whoWeServe.map((client) => (
              <span key={client} className="bg-[#121212] border border-zinc-800 px-4 py-2 text-zinc-400 text-sm">
                {client}
              </span>
            ))}
          </div>
          <p className="text-zinc-500 text-sm mt-6">
            Service Areas: Southern California and surrounding regions. Custom contracts available for ongoing security support.
          </p>
        </div>

        {/* CTA */}
        <div className="bg-[#121212] border border-[#F5C518]/30 p-8 text-center">
          <h3 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
            Upgrade Your <span className="text-[#F5C518]">Security Strategy</span>
          </h3>
          <p className="text-zinc-400 mb-6 max-w-2xl mx-auto">
            Request a consultation to determine how aerial thermal surveillance can strengthen your protection plan.
          </p>
          <Link to="/contact" className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#E5B508] transition-all duration-300">
            <span>Contact Us Today</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export const Services = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="services-page">
      <SEO 
        title="Security Services" 
        description="Explore our comprehensive security services: Executive Protection, K9 Property Guarding, Drone Surveillance (Matrice 30T), Armed/Unarmed Guards, Security Patrols, and CCTV Monitoring."
      />
      <section className="relative pt-32 pb-20 bg-[#121212]" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-4">PPO #15982 • Licensed & Insured</p>
            <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tighter text-white mb-6 font-['Barlow_Condensed']">
              Our Security <span className="text-[#F5C518]">Services</span>
            </h1>
            <p className="text-lg text-zinc-400 leading-relaxed">
              Comprehensive security solutions tailored to your needs. From executive protection to advanced drone surveillance.
            </p>
          </div>
        </div>
      </section>

      <section className="py-0" data-testid="services-list">
        {/* Expanded Executive Protection Section */}
        <ExecutiveProtectionSection />
        {/* Expanded K9 Section */}
        <K9Section />
        
        {/* Expanded Drone Section */}
        <DroneSection />
        
        {/* Expanded Armed & Unarmed Section */}
        <ArmedUnarmedSection />

        {/* Expanded Patrol Section */}
        <PatrolSection />

        {/* Expanded CCTV Section */}
        <CCTVSection />
      </section>

      <section className="py-24 bg-[#121212] border-t border-zinc-800" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F5C518]/10 text-[#F5C518] mb-6">
            <Shield className="h-8 w-8" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white font-['Barlow_Condensed'] mb-4">
            Need a Custom Security Solution?
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto mb-8">Contact our team to discuss your specific security requirements.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#E5B508] transition-all duration-300" data-testid="services-get-quote">
              <span>Get Free Quote</span>
            </Link>
            <a href="tel:714-588-2460" className="inline-flex items-center justify-center border border-zinc-700 text-white hover:border-[#F5C518] hover:text-[#F5C518] font-bold uppercase tracking-wider px-8 py-4 transition-all duration-300" data-testid="services-call-now">
              <Phone className="h-5 w-5 mr-2" />
              <span>714-588-2460</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
